/**
 * CycleDetector Service - Zustand Store
 * Single Responsibility: Detect circular form references
 *
 * A child form's own child forms are not on the FormEngine; they are in the schema the registry
 * holds for that child (under the scope of the container that contains it). Each form-container
 * inside that schema is a further scope: its children are registered under the container's id.
 */

import { create } from 'zustand';
import { useChildFormRegistryStore, childFormKey } from './ChildFormRegistryStore';
import type { ChildFormConfig } from '../types/index';

export interface CycleDetectorStore {
  /**
   * Check if a form has circular reference to itself
   * @param scope id of the container whose child forms are `childFormConfigs`
   */
  detect: (
    formID: string,
    childFormConfigs: ChildFormConfig[],
    visited?: Set<string>,
    scope?: string
  ) => boolean;

  /**
   * Get path to cycle (for error reporting)
   * @param scope id of the container whose child forms are `childFormConfigs`
   */
  getPath: (
    formID: string,
    childFormConfigs: ChildFormConfig[],
    visited?: string[],
    scope?: string
  ) => string[];
}

/** A form-container found in a child form's schema: its id (the scope of its children) and its child forms */
interface NestedContainer {
  scope: string;
  childForms: ChildFormConfig[];
}

function findContainers(controls: unknown[] | undefined): NestedContainer[] {
  const found: NestedContainer[] = [];
  for (const control of (controls ?? []) as Array<{ id: string; type: string; config?: any }>) {
    if (control.type === 'form-container') {
      found.push({ scope: control.id, childForms: control.config?.childForms ?? [] });
    }
    // Layout controls (e.g. card containers) hold their own controls
    if (Array.isArray(control.config?.controls)) {
      found.push(...findContainers(control.config.controls));
    }
  }
  return found;
}

/** Form-containers inside a child form, read from the schema registered under `scope` */
function containersInChild(scope: string, formID: string): NestedContainer[] {
  const schema = useChildFormRegistryStore.getState().schemas.get(childFormKey(scope, formID));
  return schema ? findContainers(schema.controls) : [];
}

export const useCycleDetectorStore = create<CycleDetectorStore>((set, get) => ({
  detect: (
    formID: string,
    childFormConfigs: ChildFormConfig[],
    visited = new Set<string>(),
    scope = ''
  ): boolean => {
    // If we've seen this form before, we have a cycle
    if (visited.has(formID)) {
      return true;
    }

    visited.add(formID);

    for (const childConfig of childFormConfigs) {
      // A child already on the chain is a cycle, even when it has no containers of its own
      if (visited.has(childConfig.formID)) {
        return true;
      }
      for (const nested of containersInChild(scope, childConfig.formID)) {
        if (get().detect(childConfig.formID, nested.childForms, new Set(visited), nested.scope)) {
          return true;
        }
      }
    }

    return false;
  },

  getPath: (
    formID: string,
    childFormConfigs: ChildFormConfig[],
    visited: string[] = [],
    scope = ''
  ): string[] => {
    // If we've seen this form before, we found the cycle
    if (visited.includes(formID)) {
      return [...visited, formID];
    }

    const newPath = [...visited, formID];

    for (const childConfig of childFormConfigs) {
      if (newPath.includes(childConfig.formID)) {
        return [...newPath, childConfig.formID];
      }
      for (const nested of containersInChild(scope, childConfig.formID)) {
        const cyclePath = get().getPath(childConfig.formID, nested.childForms, newPath, nested.scope);
        if (cyclePath.length > newPath.length) {
          return cyclePath;
        }
      }
    }

    return [];
  },
}));
